import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { fileToCompressedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { openPdf } from "@/pages/_hr/download";
import { PlusIcon, FilePdfIcon, PaperclipIcon, CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";

const card = "bg-card border border-border/60 rounded-lg p-4";
const lbl = "text-[9px] uppercase tracking-wider text-text-muted mb-1 block";
const inp = "w-full bg-app border border-border rounded px-2 py-1.5 text-sm";

export async function uploadRespaldo(url, file, descripcion = "") {
  const data = await fileToCompressedDataUrl(file);
  return api.post(url, { url: data, descripcion: descripcion || file.name, original_filename: file.name });
}

function Saldos({ s }) {
  if (!s) return null;
  const Item = ({ l, v, c = "text-text-primary" }) => <div><div className="text-[10px] text-text-muted">{l}</div><div className={`font-heading text-lg ${c}`}>{v}</div></div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="mis-saldos">
      <Item l="Vacaciones disponibles" v={`${s.vacaciones.dias_disponibles} d`} c="text-agro-green" />
      <Item l="Vacaciones tomadas" v={`${s.vacaciones.dias_tomados} d`} />
      <Item l="Permisos c/goce disp." v={`${s.permisos.con_goce_disponibles} d`} c="text-agro-yellow" />
      <Item l="Permisos s/goce (año)" v={`${s.permisos.sin_goce_tomados} d`} />
      <Item l="Incapacidades (año)" v={`${s.incapacidades.dias_tomados} d`} />
      <Item l="Antigüedad" v={`${s.vacaciones.anios_servicio} años`} />
    </div>
  );
}

function NuevaSolicitud({ onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ tipo: "permiso", subtipo: "con_goce", motivo: "", fecha_inicio: today, fecha_fin: today });
  const [file, setFile] = useState(null);
  const save = async () => {
    if (!f.motivo.trim()) return toast.error("Indica el motivo");
    try {
      const { data } = await api.post("/hr/absences", f);
      if (file) await uploadRespaldo(`/hr/absences/${data.id}/respaldo`, file, f.tipo === "incapacidad" ? "Constancia médica" : "");
      toast.success("Solicitud enviada a tu jefe / RRHH");
      onDone();
    } catch (e) { toast.error(apiErr(e) || e.message); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-app/40 border border-border/60 rounded p-3" data-testid="nueva-solicitud">
      <div><label className={lbl}>Tipo</label>
        <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value, subtipo: e.target.value === "permiso" ? "con_goce" : e.target.value === "incapacidad" ? "comun" : "" })} className={inp} data-testid="ms-tipo">
          <option value="permiso">Permiso</option><option value="vacaciones">Vacaciones</option><option value="incapacidad">Incapacidad</option>
        </select></div>
      {f.tipo === "permiso" && <div><label className={lbl}>Goce</label><select value={f.subtipo} onChange={(e) => setF({ ...f, subtipo: e.target.value })} className={inp}><option value="con_goce">Con goce</option><option value="sin_goce">Sin goce</option></select></div>}
      {f.tipo === "incapacidad" && <div><label className={lbl}>Tipo</label><select value={f.subtipo} onChange={(e) => setF({ ...f, subtipo: e.target.value })} className={inp}><option value="comun">Común</option><option value="maternidad">Maternidad</option><option value="riesgo_profesional">Riesgo profesional</option></select></div>}
      <div><label className={lbl}>Desde</label><input type="date" value={f.fecha_inicio} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} className={inp} /></div>
      <div><label className={lbl}>Hasta</label><input type="date" value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} className={inp} /></div>
      <div className="md:col-span-2"><label className={lbl}>Motivo</label><input value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className={inp} data-testid="ms-motivo" /></div>
      <div className="md:col-span-2"><label className={lbl}>Respaldo (constancia médica, etc.)</label><input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs" /></div>
      <button onClick={save} className="bg-agro-green text-black font-medium rounded px-3 py-1.5 text-sm" data-testid="ms-enviar">Enviar</button>
    </div>
  );
}

function AusenciasList({ rows, onReload, canManage }) {
  const act = async (a, kind) => {
    try {
      if (kind === "deny") {
        const motivo = window.prompt(`Motivo del rechazo (${a.empleado_nombre}):`);
        if (!motivo) return;
        await api.post(`/hr/absences/${a.id}/deny`, { motivo });
      } else await api.post(`/hr/absences/${a.id}/approve`);
      toast.success(kind === "deny" ? "Denegada" : "Aprobada"); onReload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const attach = async (a, file) => {
    if (!file) return;
    try { await uploadRespaldo(`/hr/absences/${a.id}/respaldo`, file); toast.success("Respaldo adjuntado"); onReload(); }
    catch (e) { toast.error(apiErr(e) || e.message); }
  };
  return (
    <table className="w-full text-xs">
      <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase text-text-muted">
        {canManage && <th className="p-2">Empleado</th>}<th className="p-2">Tipo</th><th className="p-2">Fechas</th><th className="p-2 text-right">Días</th><th className="p-2">Estado</th><th className="p-2">Respaldo</th><th className="p-2 text-right">Acciones</th>
      </tr></thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan="7" className="p-4 text-center text-text-muted">Sin solicitudes</td></tr> : rows.map((a) => (
          <tr key={a.id} className="border-t border-border/30">
            {canManage && <td className="p-2">{a.empleado_nombre}</td>}
            <td className="p-2 capitalize">{a.tipo}{a.subtipo ? ` · ${a.subtipo.replace("_", " ")}` : ""}</td>
            <td className="p-2">{fmtDate(a.fecha_inicio)} → {fmtDate(a.fecha_fin)}</td>
            <td className="p-2 text-right">{a.dias}</td>
            <td className="p-2 uppercase text-[10px]">{a.estado}{a.denegado_motivo ? ` · ${a.denegado_motivo}` : ""}</td>
            <td className="p-2">{(a.respaldos || []).length || (a.respaldo_url ? 1 : 0)} <PaperclipIcon size={11} className="inline" />
              {!canManage && a.estado === "pendiente" && <label className="ml-2 text-agro-green cursor-pointer">+<input type="file" hidden accept="image/*,.pdf" onChange={(e) => attach(a, e.target.files?.[0])} /></label>}</td>
            <td className="p-2 text-right">{canManage && a.estado === "pendiente" && <span className="inline-flex gap-1">
              <button onClick={() => act(a, "approve")} className="text-agro-green" title="Aprobar" aria-label="Aprobar"><CheckCircleIcon size={14} weight="bold" /></button>
              <button onClick={() => act(a, "deny")} className="text-red-400" title="Denegar" aria-label="Denegar"><XCircleIcon size={14} weight="bold" /></button></span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Autoservicio (13.6): perfil, saldos, colillas, solicitudes y actas del usuario logueado.
export default function MiPerfilTab() {
  const [me, setMe] = useState(null);
  const [nueva, setNueva] = useState(false);
  const load = () => api.get("/hr/me").then(({ data }) => setMe(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!me) return <div className="p-10 text-center text-text-muted">Cargando…</div>;
  const e = me.employee;
  if (!e) return <div className={`${card} text-sm text-text-muted`} data-testid="mi-perfil-sin-vinculo">Tu usuario no está vinculado a un expediente de empleado. Solicita a RRHH que vincule tu usuario.</div>;
  return (
    <div className="space-y-4" data-testid="mi-perfil">
      <div className={card}>
        <div className="flex justify-between flex-wrap gap-2">
          <div><div className="text-[10px] uppercase text-text-muted">Mi expediente</div><h3 className="font-heading text-xl">{e.nombre}</h3>
            <div className="text-xs text-text-secondary">{e.codigo} · {e.puesto || "—"} · {e.departamento || "—"} · Ingreso {fmtDate(e.fecha_ingreso)}</div></div>
          <div className="text-xs text-right text-text-secondary">Estado: <b className="uppercase">{e.estado}</b><br />Correo: {e.email_institucional || e.email || "—"}<br />Salario base: {e.salario_base != null ? fmtL(e.salario_base) : "—"}</div>
        </div>
        <div className="mt-4"><Saldos s={me.saldos} /></div>
      </div>
      <div className={card}>
        <div className="flex justify-between items-center mb-3"><h4 className="font-heading text-sm">Mis solicitudes (permisos, vacaciones, incapacidades)</h4>
          <button onClick={() => setNueva(!nueva)} className="text-xs flex items-center gap-1 px-3 py-1.5 bg-agro-green text-black rounded" data-testid="ms-nueva"><PlusIcon size={12} /> Nueva solicitud</button></div>
        {nueva && <div className="mb-3"><NuevaSolicitud onDone={() => { setNueva(false); load(); }} /></div>}
        <AusenciasList rows={me.ausencias} onReload={load} />
      </div>
      {me.subordinados?.length > 0 && (
        <div className={card} data-testid="equipo">
          <h4 className="font-heading text-sm mb-3">Solicitudes de mi equipo ({me.subordinados.length} colaboradores)</h4>
          <AusenciasList rows={me.ausencias_equipo || []} onReload={load} canManage />
        </div>
      )}
      <div className={card}>
        <h4 className="font-heading text-sm mb-3">Mis colillas de pago</h4>
        {me.colillas.length === 0 ? <div className="text-xs text-text-muted">Aún no hay planillas aprobadas.</div> : (
          <table className="w-full text-xs"><tbody>{me.colillas.map((c) => (
            <tr key={c.id} className="border-t border-border/30">
              <td className="p-2">{c.periodo}</td><td className="p-2 text-text-muted">{fmtDate(c.fecha_inicio)} → {fmtDate(c.fecha_fin)}</td>
              <td className="p-2 uppercase text-[10px]">{c.estado}</td><td className="p-2 text-right text-agro-green">{fmtL(c.neto_a_pagar)}</td>
              <td className="p-2 text-right"><button onClick={() => openPdf(`/hr/payroll/lines/${c.id}/pdf`)} className="text-agro-yellow inline-flex items-center gap-1" data-testid={`colilla-${c.id}`}><FilePdfIcon size={14} /> PDF</button></td>
            </tr>))}</tbody></table>
        )}
      </div>
      <div className={card}>
        <h4 className="font-heading text-sm mb-3">Mis actas</h4>
        {me.actas.length === 0 ? <div className="text-xs text-text-muted">Sin actas registradas.</div> : me.actas.map((a) => (
          <div key={a.id} className="flex justify-between border-t border-border/30 py-2 text-xs">
            <span><b className="uppercase">{a.tipo.replace(/_/g, " ")}</b> · {a.motivo} · {fmtDate(a.fecha)}</span>
            <button onClick={() => openPdf(`/hr/acts/${a.id}/pdf`)} className="text-agro-yellow inline-flex items-center gap-1"><FilePdfIcon size={14} /> Constancia</button>
          </div>
        ))}
      </div>
      {me.prestamos?.length > 0 && (
        <div className={card}><h4 className="font-heading text-sm mb-2">Mis préstamos</h4>
          {me.prestamos.map((p) => <div key={p.id} className="text-xs border-t border-border/30 py-1">{p.numero} · Monto {fmtL(p.monto)} · Cuota {fmtL(p.cuota)} · Saldo <b>{fmtL(p.saldo)}</b> · {p.estado}</div>)}
        </div>
      )}
    </div>
  );
}
